/**
 * SKILL-BASED PROFICIENCY SPLITTING (Decision Helpers)
 * 
 * Renamed from ticketSplitting.ts (R45 FIX-10) to reflect that this module
 * only contains analysis/suggestion logic — it does NOT mutate tickets.
 * Server-side split execution lives in:
 *   - supabase/functions/split-ticket/index.ts
 *   - supabase/functions/execute-manual-split/index.ts
 */

interface Job {
  id: string;
  category: string;
  submenu: string;
  name: string;
  skill_level: number;
  standard_minutes: number;
  bay_requirement: string;
}

interface Technician {
  id: string;
  name: string;
  proficiencies: Record<string, number>;
  skill_tier: number;
}

interface SplitDecision {
  shouldSplit: boolean;
  splitGroups?: Array<{
    jobs: Job[];
    requiredCategory?: string;
    suggestedTechId?: string;
    reasoning: string;
  }>;
}

/**
 * Analyzes jobs on a ticket to determine if they should be split between technicians
 * based on skill level gaps AND proficiency category gaps
 */
export async function analyzeTicketSplitting(
  jobs: Job[],
  technicians?: Technician[],
  specialistGapThreshold: number = 15
): Promise<SplitDecision> {
  if (jobs.length < 2) {
    return { shouldSplit: false };
  }

  // 1. Check for SKILL LEVEL gaps
  const skillLevels = jobs.map(j => j.skill_level);
  const maxSkill = Math.max(...skillLevels);
  const minSkill = Math.min(...skillLevels);
  const skillGap = maxSkill - minSkill;

  // 2. Check for PROFICIENCY CATEGORY separation
  const categories = [...new Set(jobs.map(j => j.category))];
  let maxProficiencyGap = 0;

  // 3. If technicians provided, calculate proficiency gaps
  if (technicians && technicians.length > 0) {
    for (const tech of technicians) {
      const categoryProfs = categories.map(cat => tech.proficiencies[cat] || 0);
      if (categoryProfs.length > 0) {
        const gap = Math.max(...categoryProfs) - Math.min(...categoryProfs);
        maxProficiencyGap = Math.max(maxProficiencyGap, gap);
      }
    }
  }

  // Decision: Split if EITHER skill gap ≥2 OR proficiency gap ≥ threshold
  const shouldSplit = skillGap >= 2 || maxProficiencyGap >= specialistGapThreshold;

  if (!shouldSplit) {
    return { shouldSplit: false };
  }

  // Group jobs by proficiency category if we have categories, otherwise by skill level
  if (categories.length > 1) {
    const groupedByCategory: Record<string, Job[]> = {};
    jobs.forEach(job => {
      const cat = job.category;
      if (!groupedByCategory[cat]) groupedByCategory[cat] = [];
      groupedByCategory[cat].push(job);
    });

    // Create split groups (max 2)
    const splitGroups = Object.entries(groupedByCategory)
      .sort((a, b) => b[1].length - a[1].length) // Largest groups first
      .slice(0, 2)
      .map(([category, categoryJobs]) => ({
        jobs: categoryJobs,
        requiredCategory: category,
        reasoning: `Specialist work: ${category} (${categoryJobs.length} jobs)`,
      }));

    if (splitGroups.length >= 2) {
      return { shouldSplit: true, splitGroups };
    }
  }

  // Fallback to skill level grouping
  const diagnosticJobs = jobs.filter(j => j.skill_level >= 4); // Advanced/Expert
  const standardJobs = jobs.filter(j => j.skill_level <= 2); // Basic/Intermediate
  const intermediateJobs = jobs.filter(j => j.skill_level === 3);

  if (diagnosticJobs.length > 0 && standardJobs.length > 0) {
    let group1Jobs = [...diagnosticJobs];
    let group2Jobs = [...standardJobs];
    
    if (diagnosticJobs.length > standardJobs.length) {
      group1Jobs = [...diagnosticJobs, ...intermediateJobs];
    } else {
      group2Jobs = [...standardJobs, ...intermediateJobs];
    }

    return {
      shouldSplit: true,
      splitGroups: [
        {
          jobs: group1Jobs,
          reasoning: 'High-skill diagnostic/advanced work',
        },
        {
          jobs: group2Jobs,
          reasoning: 'Standard maintenance/basic work',
        },
      ],
    };
  }

  return { shouldSplit: false };
}

/**
 * Determines if a ticket ID indicates it's a split ticket
 */
export function isTicketSplit(ticketId: string): boolean {
  return ticketId.includes('-SPLIT-');
}
